/**
 * Day-parting — the 7×24 hour matrix from the legacy rtb_ui. Rows are Sunday–Saturday, columns are hours
 * 00–23. Click a cell to toggle that weekday/hour; click an hour header to toggle that hour for every
 * weekday (fully-on clears, otherwise selects); click a weekday header to toggle all 24 hours for that day.
 * The 24-char mask is authoritative — start/end/status are derived from it. Emits the normalized schedule.
 */

import { useMemo } from 'react';
import {
  DAY_PARTING_DAYS, ALL_HOURS, EMPTY_HOURS, singleFromHours,
  type DayName, type DayPartingSchedule,
} from '../lib/day-parting';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const SHORT: Record<DayName, string> = {
  sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
};
const setChar = (s: string, i: number, ch: string): string => s.slice(0, i) + ch + s.slice(i + 1);

export function DayPartingPicker(
  { value, onChange, disabled = false }: { value: DayPartingSchedule; onChange: (v: DayPartingSchedule) => void; disabled?: boolean },
): JSX.Element {
  const masks = useMemo(() => {
    const m = {} as Record<DayName, string>;
    for (const d of DAY_PARTING_DAYS) m[d] = value[d]?.hours ?? EMPTY_HOURS;
    return m;
  }, [value]);

  const emit = (next: Record<DayName, string>) => {
    const out = {} as DayPartingSchedule;
    for (const d of DAY_PARTING_DAYS) out[d] = singleFromHours(next[d]);
    onChange(out);
  };

  const toggleCell = (day: DayName, hour: number) => {
    if (disabled) return;
    emit({ ...masks, [day]: setChar(masks[day], hour, masks[day][hour] === '1' ? '0' : '1') });
  };
  const toggleColumn = (hour: number) => {
    if (disabled) return;
    const allOn = DAY_PARTING_DAYS.every((d) => masks[d][hour] === '1');
    const next = { ...masks };
    for (const d of DAY_PARTING_DAYS) next[d] = setChar(next[d], hour, allOn ? '0' : '1');
    emit(next);
  };
  const toggleRow = (day: DayName) => {
    if (disabled) return;
    emit({ ...masks, [day]: masks[day] === ALL_HOURS ? EMPTY_HOURS : ALL_HOURS });
  };

  return (
    <div className={`dp-matrix-wrap${disabled ? ' disabled' : ''}`}>
      <table className="dp-matrix">
        <thead>
          <tr>
            <th className="dp-corner" />
            {HOURS.map((h) => (
              <th key={h}>
                <button type="button" className="dp-hhead" onClick={() => toggleColumn(h)} title={`Toggle ${String(h).padStart(2, '0')}:00 for all days`}>
                  {String(h).padStart(2, '0')}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAY_PARTING_DAYS.map((d) => {
            const active = value[d]?.status === 'on';
            return (
              <tr key={d} className={active ? '' : 'dp-off'}>
                <th className="dp-dhead">
                  <button type="button" onClick={() => toggleRow(d)} title={`Toggle all hours for ${SHORT[d]}`}>{SHORT[d]}</button>
                </th>
                {HOURS.map((h) => (
                  <td key={h}>
                    <button
                      type="button"
                      className={`dp-cell${masks[d][h] === '1' ? ' on' : ''}`}
                      onClick={() => toggleCell(d, h)}
                      aria-pressed={masks[d][h] === '1'}
                      aria-label={`${SHORT[d]} ${String(h).padStart(2, '0')}:00`}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="dp-legend">Click a cell to toggle an hour, an hour header to toggle a column, or a day to toggle the whole row.</p>
    </div>
  );
}

export { defaultSchedule as DEFAULT_DAY_PARTING_SCHEDULE } from '../lib/day-parting';
